import { useMemo } from 'react';
import { Button, ButtonVariant } from '../../elements/Button';
import { ImportLocationsHeader } from './ImportLocationsHeader';
import { ImportLocationsResponse } from '../../../types/location';
import LocationsImportReportTable from './LocationsImportReportTable';
import UploadErrors from './UploadErrors';
import { ErrorInterface } from '../../../types/errors';
import { Divider } from '../../elements/Divider';
import {
  ImportPhase,
  LocationImportStatisticsWithTitle,
} from './LocationImportStatisticsWithTitle';

interface LocationsUploadResultsProps {
  onClose: () => void;
  onUploadNewCSV: () => void;
  createLocationsResult?: ImportLocationsResponse;
  createLocationsErrors?: ErrorInterface[];
  locationsCount: number;
}

const LocationsUploadResults = ({
  onClose,
  onUploadNewCSV,
  createLocationsResult,
  createLocationsErrors,
  locationsCount,
}: LocationsUploadResultsProps) => {
  const createdLocations = useMemo(() => {
    return createLocationsResult?.data?.filter((location) =>
      createLocationsResult.details?.locations.created?.includes(location.name),
    );
  }, [createLocationsResult]);

  const updatedLocations = useMemo(() => {
    return createLocationsResult?.data?.filter((location) =>
      createLocationsResult.details?.locations.updated?.includes(location.name),
    );
  }, [createLocationsResult]);

  return (
    <div className='flex flex-col font-sans w-full h-full justify-between items-center'>
      <ImportLocationsHeader
        title='Upload Results'
        description='Below are the results for your CSV upload.'
      />
      <div className='p-4 w-full'>
        {!!createLocationsResult && (
          <>
            <LocationImportStatisticsWithTitle
              title={'Successes'}
              uploadedLocationsCount={locationsCount}
              locationsReport={{
                existing_locations: updatedLocations?.map(
                  (location) => location.name,
                ),
                new_locations: createdLocations?.map(
                  (location) => location.name,
                ),
              }}
              stateOfImport={ImportPhase.END}
            />
            <LocationsImportReportTable
              createdLocations={createdLocations}
              updatedLocations={updatedLocations}
            />
            {!!createLocationsErrors?.length && <Divider customStyle='mb-4' />}
          </>
        )}
        {!!createLocationsErrors?.length && (
          <>
            <LocationImportStatisticsWithTitle
              title={'Errors'}
              uploadedLocationsCount={locationsCount}
              locationsReport={null}
              errors={createLocationsErrors}
              stateOfImport={ImportPhase.END}
            />
            <UploadErrors errors={createLocationsErrors} />
          </>
        )}
      </div>
      <div className='w-full flex p-4 pt-0 justify-between'>
        <Button label='Close' onClick={onClose} variant={ButtonVariant.Ghost} />
        <Button
          variant={ButtonVariant.Default}
          label='Upload New CSV'
          className='w-[150px]'
          onClick={onUploadNewCSV}
        />
      </div>
    </div>
  );
};

export default LocationsUploadResults;
